import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';
import { Plus, Pencil, Trash2, Save, X, Eye } from 'lucide-react';
import { toast } from '../lib/toast';
import { Database } from '../types/supabase';
import NoteDetails from '../components/NoteDetails';

type Agency = Database['public']['Tables']['agencies']['Row'];

interface Note {
  id: string;
  title: string;
  content: string;
  category: 'personal' | 'meeting';
  reminder_at: string | null;
  reminder_status: 'pending' | 'sent' | 'cancelled';
  created_at: string;
}

interface NotesProps {
  navigate: (to: string) => void;
}

const EXCERPT_LENGTH = 150; // Number of characters to show in excerpt

export default function Notes({ navigate }: NotesProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<'personal' | 'meeting'>('personal');
  const [reminderAt, setReminderAt] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('');

  useEffect(() => {
    fetchNotes();
    fetchAgencies();
  }, []);

  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('notes_with_agencies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      console.log('Fetched notes:', data);
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No authenticated user found');
        setAgencies([]);
        return;
      }
      
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, contact_first_name, contact_last_name, email, phone, created_at, logo_url, additional_info')
        .eq('user_id', user.id)
        .order('name');

      if (error) {
        console.error('Error fetching agencies:', error);
        toast.error('Failed to load customer data');
        setAgencies([]);
        return;
      }
      
      console.log('Fetched agencies:', data);
      setAgencies(data || []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error('Failed to load customer data');
      setAgencies([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    // Combine date and time for reminder
    let reminder = null;
    if (reminderAt && reminderTime) {
      const reminderDate = new Date(`${reminderAt}T${reminderTime}`);
      // Only set reminder if it's in the future
      if (reminderDate > new Date()) {
        reminder = reminderDate.toISOString();
      } else {
        toast.error('Reminder time must be in the future');
        return;
      }
    }

    try {
      if (editingNote) {
        const { error } = await supabase
          .from('notes')
          .update({
            title: title.trim(),
            content: content.trim(),
            category,
            agency_id: category === 'meeting' ? selectedAgencyId : null,
            reminder_at: reminder,
            reminder_status: reminder ? 'pending' : null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingNote.id);

        if (error) throw error;
        toast.success('Note updated successfully');
      } else {
        const { error } = await supabase
          .from('notes')
          .insert([{
            title: title.trim(),
            content: content.trim(),
            category,
            agency_id: category === 'meeting' ? selectedAgencyId : null,
            reminder_at: reminder,
            reminder_status: reminder ? 'pending' : null,
            user_id: (await supabase.auth.getUser()).data.user?.id
          }]);

        if (error) throw error;
        toast.success('Note created successfully');
      }

      setTitle('');
      setContent('');
      setCategory('personal');
      setReminderAt('');
      setReminderTime('');
      setSelectedAgencyId('');
      setEditingNote(null);
      setShowForm(false);
      fetchNotes();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Failed to save note');
    }
  };

  const handleEdit = (note: Note) => {
    setShowForm(true);
    setEditingNote(note);
    setTitle(note.title);
    setContent(note.content);
    setCategory(note.category);
    // Set the agency ID if it exists
    // Check both note.agency_id and note.agencies?.id
    const agencyId = note.agency_id || (note.agencies ? note.agencies.id : null);
    if (agencyId) {
      setSelectedAgencyId(agencyId);
    } else {
      setSelectedAgencyId('');
    }
    if (note.reminder_at) {
      const reminder = new Date(note.reminder_at);
      setReminderAt(reminder.toISOString().split('T')[0]);
      setReminderTime(reminder.toTimeString().split(':').slice(0, 2).join(':'));
    } else {
      setReminderAt('');
      setReminderTime('');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setNotes(notes.filter(note => note.id !== id));
      toast.success('Note deleted successfully');
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const truncateContent = (content: string) => {
    if (content.length <= EXCERPT_LENGTH) return content;
    return content.substring(0, EXCERPT_LENGTH) + '...';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">My Notes</h1>

          <div className="flex justify-end mb-8">
            <button
              onClick={() => {
                setShowForm(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Note
            </button>
          </div>

          {showForm && <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6 mb-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter note title"
                  className="mt-1 block w-full rounded-md"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Category
                </label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as 'personal' | 'meeting')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="personal">Personal Note</option>
                  <option value="meeting">Meeting Note</option>
                </select>
              </div>

              {category === 'meeting' && (
                <div>
                  <label htmlFor="agency" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Customer
                  </label>
                  {/* Debug info - remove in production */}
                  {/* <div className="text-xs text-gray-500 mb-1">
                    Available agencies: {agencies?.length || 0}
                  </div> */}
                  <select
                    id="agency"
                    value={selectedAgencyId}
                    onChange={(e) => setSelectedAgencyId(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Select a customer</option>
                    {agencies && agencies.length > 0 ? (
                      agencies.map((agency) => (
                        <option key={agency.id} value={agency.id}>
                          {agency.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No customers available</option>
                    )}
                  </select>
                  {agencies && agencies.length === 0 && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      No customers found. You can still create a personal note.
                    </p>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Content
                </label>
                <textarea
                  id="content"
                  rows={4}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter note content"
                  className="mt-1 block w-full rounded-md"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="reminder-date" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Reminder Date
                  </label>
                  <input
                    type="date"
                    id="reminder-date"
                    value={reminderAt}
                    onChange={(e) => setReminderAt(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-1 block w-full rounded-md"
                  />
                </div>
                <div>
                  <label htmlFor="reminder-time" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Reminder Time
                  </label>
                  <input
                    type="time"
                    id="reminder-time"
                    value={reminderTime}
                   onChange={(e) => setReminderTime(e.target.value)} 
                   className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditingNote(null);
                    setTitle('');
                    setContent('');
                    setCategory('personal');
                    setReminderAt('');
                    setReminderTime('');
                    setSelectedAgencyId('');
                    setShowForm(false);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  {editingNote ? (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Update Note
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Note
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>}

          {/* Notes List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                No notes found. Create one to get started!
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {note.title}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${
                        note.category === 'meeting' 
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                      }`}>
                        {note.category === 'meeting' ? 'Meeting Note' : 'Personal Note'}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedNote(note)}
                        className="text-gray-400 hover:text-indigo-500"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(note)}
                        className="text-gray-400 hover:text-indigo-500"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 whitespace-pre-wrap line-clamp-3">
                    {truncateContent(note.content)}
                  </p>
                  <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                    {new Date(note.created_at).toLocaleString()} 
                    {note.category === 'meeting' && note.agency_name && (
                      <span className="ml-2 text-indigo-500 dark:text-indigo-400" title="Customer">
                        • {note.agency_name}
                      </span>
                    )}
                  </p>
                  {note.reminder_at && (
                    <div className="mt-2 flex items-center">
                      <span className={`text-xs px-2 py-1 rounded ${
                        note.reminder_status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : note.reminder_status === 'sent'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        Reminder: {new Date(note.reminder_at).toLocaleString()}
                        {note.reminder_status !== 'pending' && ` (${note.reminder_status})`}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      {selectedNote && (
        <NoteDetails
          note={selectedNote}
          onClose={() => setSelectedNote(null)}
        />
      )}
    </div>
  );
}